// Pronoun tables (नि. १–७, २७, ३१, ३६, ४२, ४६, ५१), ported from tools/generate_paradigms.py.
// Tables carry seven cases (प्रथमा … सप्तमी); there is no सम्बोधन row.

import type {
    Gender,
    Num,
    Person,
    PronounTable
} from '@/prakrit/lib/grammar/types'

import { F } from '@/prakrit/lib/grammar/verbs'
import { PRONOUN_CASES } from '@/prakrit/lib/grammar/types'

type Row = [eka: string, bahu: string]

const P = (rows: Row[]): PronounTable => {
    const out = {} as PronounTable

    PRONOUN_CASES.forEach((c, i) => {
        out[c] = { eka: [F(rows[i][0])], bahu: [F(rows[i][1])] }
    })

    return out
}

const clone = (t: PronounTable): PronounTable => JSON.parse(JSON.stringify(t)) as PronounTable

const build = (): Record<string, PronounTable> => {
    const tables: Record<string, PronounTable> = {
        'अम्ह (मैं)': P([['अहं', 'अम्हे'], ['ममं', 'अम्हे'], ['मए', 'अम्हेहि'], ['मज्झ', 'अम्हाण'],
            ['ममाओ', 'अम्हाहिंतो'], ['मज्झ', 'अम्हाण'], ['अम्हम्मि', 'अम्हेसु']]),
        'तुम्ह (तुम)': P([['तुमं', 'तुम्हे'], ['तुमं', 'तुम्हे'], ['तुमए', 'तुम्हेहि'], ['तुज्झ', 'तुम्हाण'],
            ['तुमाओ', 'तुम्हाहिंतो'], ['तुज्झ', 'तुम्हाण'], ['तुम्हम्मि', 'तुम्हेसु']]),
        'त (वह, पु.)': P([['सो', 'ते'], ['तं', 'ते'], ['तेण', 'तेहि'], ['तस्स', 'ताण'],
            ['ताओ', 'ताहिंतो'], ['तस्स', 'ताण'], ['तम्मि', 'तेसु']]),
        'ता (वह, स्त्री.)': P([['सा', 'ताओ'], ['तं', 'ताओ'], ['ताए', 'ताहि'], ['ताअ', 'ताण'],
            ['तत्तो', 'ताहिंतो'], ['ताअ', 'ताण'], ['ताए', 'तासु']]),
        'इम (यह, पु.)': P([['इमो', 'इमे'], ['इमं', 'इमे'], ['इमेण', 'इमेहि'], ['इमस्स', 'इमाण'],
            ['इमाओ', 'इमाहिंतो'], ['इमस्स', 'इमाण'], ['इमम्मि', 'इमेसु']]),
        'इमा (यह, स्त्री.)': P([['इमा', 'इमाओ'], ['इमं', 'इमाओ'], ['इमाए', 'इमाहि'], ['इमाअ', 'इमाण'],
            ['इमत्तो', 'इमाहिंतो'], ['इमाअ', 'इमाण'], ['इमाए', 'इमासु']]),
        'क (कौन, पु.)': P([['को', 'के'], ['कं', 'के'], ['केण', 'केहि'], ['कस्स', 'काण'],
            ['काओ', 'काहिंतो'], ['कस्स', 'काण'], ['कम्मि', 'केसु']]),
        'का (कौन, स्त्री.)': P([['का', 'काओ'], ['कं', 'काओ'], ['काए', 'काहि'], ['काअ', 'काण'],
            ['कत्तो', 'काहिंतो'], ['काअ', 'काण'], ['काए', 'कासु']])
    }

    // नपुंसक: प्रथमा/द्वितीया अलग, शेष पुल्लिंग जैसे (नि. २६, ३०, ३५)
    const taNapum = clone(tables['त (वह, पु.)'])

    for (const c of ['prathama', 'dvitiya'] as const) taNapum[c] = { eka: [F('तं')], bahu: [F('ताणि')] }

    tables['त (वह, नपुं.)'] = taNapum

    const imaNapum = clone(tables['इम (यह, पु.)'])

    for (const c of ['prathama', 'dvitiya'] as const) {
        imaNapum[c] = {
            eka: [F('इमं'), F('इदं', 'alt', "पाठ ३४ के बाद के उदाहरणों में 'इदं' छपा है")],
            bahu: [F('इमाणि')]
        }
    }

    tables['इम (यह, नपुं.)'] = imaNapum

    tables['इम (यह, पु.)'].tritiya.eka.push(F('इमिणा', 'alt', 'पाठ ३५ के उदाहरण में'))

    return tables
}

/** The course book's pronoun tables, exactly as data/pronouns.json. */
export const PRONOUNS_BOOK: Record<string, PronounTable> = build()

/**
 * The subject variants of the teacher's notes (प्राकृत-रचना-सौरभ पाठ १–८), added to प्रथमा as 'alt': हं / अम्मि (= अहं),
 * वयं (= अम्हे), तुं / तुह (= तुमं), तुब्भे / तुज्झे (= तुम्हे), ताउ (= ताओ). ता (= ताओ) is left out of the tables because
 * ता is also the अव्यय "तो" of जइ … ता; the tense pages still show it (VIKALP_SUBJECTS in pratyaya.ts).
 */
const SUBJECT_VARIANTS: [table: string, num: Num, forms: string[], rule: string][] = [
    ['अम्ह (मैं)', 'eka', ['हं', 'अम्मि'], 'रचना-सौरभ पाठ १'],
    ['अम्ह (मैं)', 'bahu', ['वयं'], 'रचना-सौरभ पाठ ५'],
    ['तुम्ह (तुम)', 'eka', ['तुं', 'तुह'], 'रचना-सौरभ पाठ २'],
    ['तुम्ह (तुम)', 'bahu', ['तुब्भे', 'तुज्झे'], 'रचना-सौरभ पाठ ६'],
    ['ता (वह, स्त्री.)', 'bahu', ['ताउ'], 'रचना-सौरभ पाठ ७']
]

const withSubjectVariants = (book: Record<string, PronounTable>): Record<string, PronounTable> => {
    const out = JSON.parse(JSON.stringify(book)) as Record<string, PronounTable>

    for (const [table, num, forms, rule] of SUBJECT_VARIANTS) {
        const cell = out[table]?.prathama?.[num]

        if (!cell) continue

        for (const form of forms) {
            if (!cell.some(f => f.form === form)) cell.push(F(form, 'alt', rule))
        }
    }

    return out
}

/** The pronoun tables the app uses: the book's plus the notes' subject variants. */
export const PRONOUNS: Record<string, PronounTable> = withSubjectVariants(PRONOUNS_BOOK)

export interface PronounMeta {
    key: string
    base: string
    person: Person
    gender: Gender | null
    hi: string
    en: string
    slug: string
}

/** Display and grammar facts for each pronoun table. अम्ह and तुम्ह are the same in all genders. */
export const PRONOUN_META: PronounMeta[] = [
    { key: 'अम्ह (मैं)', base: 'अम्ह', person: 'uttam', gender: null, hi: 'मैं, हम', en: 'I, we', slug: 'amha' },
    { key: 'तुम्ह (तुम)', base: 'तुम्ह', person: 'madhyam', gender: null, hi: 'तुम, तुम सब', en: 'you, you all', slug: 'tumha' },
    { key: 'त (वह, पु.)', base: 'त', person: 'anya', gender: 'pu', hi: 'वह, वे (पुल्लिंग)', en: 'he, they (masc.)', slug: 'ta-pu' },
    { key: 'ता (वह, स्त्री.)', base: 'ता', person: 'anya', gender: 'stri', hi: 'वह, वे (स्त्रीलिंग)', en: 'she, they (fem.)', slug: 'ta-stri' },
    { key: 'त (वह, नपुं.)', base: 'त', person: 'anya', gender: 'napum', hi: 'वह, वे (नपुंसकलिंग)', en: 'it, they (neut.)', slug: 'ta-napum' },
    { key: 'इम (यह, पु.)', base: 'इम', person: 'anya', gender: 'pu', hi: 'यह, ये (पुल्लिंग)', en: 'this, these (masc.)', slug: 'ima-pu' },
    { key: 'इमा (यह, स्त्री.)', base: 'इमा', person: 'anya', gender: 'stri', hi: 'यह, ये (स्त्रीलिंग)', en: 'this, these (fem.)', slug: 'ima-stri' },
    { key: 'इम (यह, नपुं.)', base: 'इम', person: 'anya', gender: 'napum', hi: 'यह, ये (नपुंसकलिंग)', en: 'this, these (neut.)', slug: 'ima-napum' },
    { key: 'क (कौन, पु.)', base: 'क', person: 'anya', gender: 'pu', hi: 'कौन (पुल्लिंग)', en: 'who (masc.)', slug: 'ka-pu' },
    { key: 'का (कौन, स्त्री.)', base: 'का', person: 'anya', gender: 'stri', hi: 'कौन (स्त्रीलिंग)', en: 'who (fem.)', slug: 'ka-stri' }
]

export const pronounMeta = (key: string): PronounMeta | undefined => PRONOUN_META.find(m => m.key === key || m.slug === key)

export type PronounCell = { table: string, case: keyof PronounTable, number: Num }
